import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/services/api";

import { characterApi } from "./characterApi";
import type { Character } from "./types";

export const CHARACTER_QUERY_KEY = ["character", "me"] as const;

/** The signed-in player's character, or null if they haven't created one (404). */
export function useCharacter() {
  return useQuery<Character | null>({
    queryKey: CHARACTER_QUERY_KEY,
    queryFn: async ({ signal }) => {
      try {
        return await characterApi.me(signal);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
  });
}

export function useCharacterOptions() {
  return useQuery({
    queryKey: ["character", "options"],
    queryFn: ({ signal }) => characterApi.options(signal),
    staleTime: Infinity, // only changes with a deploy
  });
}

/**
 * Deliberately does NOT write the new character into the cache on success:
 * the studio first shows the "passport minted" screen, and only when the
 * player continues does the cache update (which lets the route guards move on).
 */
export function useCreateCharacter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: characterApi.create,
    onError: (error) => {
      // 409 = this account already has a character (e.g. created in another tab).
      if (error instanceof ApiError && error.status === 409) {
        void queryClient.invalidateQueries({ queryKey: CHARACTER_QUERY_KEY });
      }
    },
  });
}